import { useCallback } from 'react';

import type { Edition, Product } from '../types';

import { useOperationalAnalytics } from './use-operational-analytics';

/**
 * This hook is designed to be used before contexts are initialized to avoid circular dependencies.
 */
function useOperationalFailureAnalytics({
	product,
	edition,
}: {
	edition: Edition;
	product: Product;
}) {
	const sendOperationalEvent = useOperationalAnalytics({ product, edition });

	return useCallback(
		(attributes: Record<string, any>) => {
			sendOperationalEvent({
				action: 'failed',
				actionSubject: '@atlassian/edition-awareness.edition-awareness',
				attributes,
			});
		},
		[sendOperationalEvent],
	);
}

/**
 * These are errors which we ignore from SLO reporting to ensure we reporting on errors we can action
 */
const nonSLOErrors = [
	/**
	 * These occur when the fetch request fails (usually this is because the users network is offline)
	 *
	 * This is usually thrown from inside of the products fetch handler
	 * e.g. jira/src/packages/platform/utils/fetch/src/utils/requests.tsx adds the handler which throws this error to every fetch request
	 */
	/failed to fetch/i,
	/**
	 * These occur when the fetch request fails due to a server response (e.g. 401 due to the user needing to reauthenticate their session)
	 * e.g. jira/src/packages/platform/utils/fetch/src/utils/requests.tsx adds the handler which throws server errors after parsing the response headers
	 */
	/Error server response:/i,
];

function isSLOError(error: Error) {
	// Just checks that none of our non slo errors match the error
	return !nonSLOErrors.some((msg) => msg.test(error.message));
}

/**
 * Use for tracking errors caught in error boundaries
 */
export function useErrorAnalytics({
	product,
	edition,
	id,
}: {
	edition: Edition;
	id?: string;
	product: Product;
}): (error: Error) => void {
	const sendOperationalFailure = useOperationalFailureAnalytics({ product, edition });

	return useCallback(
		(error: Error) => {
			sendOperationalFailure({
				error: error.message,
				errorStack: error.stack,
				errorName: error.name,
				id: id ?? 'unknownErrorId',
				isSLOError: isSLOError(error),
			});
		},
		[sendOperationalFailure, id],
	);
}

/**
 * Use when data is not completely broken (not caught by error boundary),
 * but is invalid or missing required data points.
 */
export function useDataValidationErrorAnalytics({
	product,
	edition,
}: {
	edition: Edition;
	product: Product;
}): (dataValidationError: Record<string, any>) => void {
	const sendOperationalFailure = useOperationalFailureAnalytics({ product, edition });

	return useCallback(
		(dataValidationError: Record<string, any>) => {
			sendOperationalFailure({
				dataValidationError,
				id: 'dataValidationError',
			});
		},
		[sendOperationalFailure],
	);
}
